import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import * as React from 'react';

import { cn } from '../../lib/cn';

const statusBlockVariants = cva(
	'flex flex-col items-center justify-center gap-2 px-4 text-center text-[13px]',
	{
		variants: {
			kind: {
				loading: 'text-text-muted',
				error: 'text-danger',
				empty: 'rounded-xl border border-dashed border-border text-text-muted',
			},
			size: {
				sm: 'h-20',
				md: 'h-28',
				lg: 'h-40',
			},
		},
		defaultVariants: {
			kind: 'empty',
			size: 'md',
		},
	},
);

export interface StatusBlockProps extends React.HTMLAttributes<HTMLDivElement> {
	/** Tipo di stato: `loading` (spinner), `error` (colore danger, `role="alert"`) o `empty` (bordo tratteggiato). Default: `empty`. */
	kind?: VariantProps<typeof statusBlockVariants>['kind'];
	/** Altezza del blocco. Default: `md`. */
	size?: VariantProps<typeof statusBlockVariants>['size'];
	/** Icona mostrata sopra il messaggio (solo stato `empty`); lo stato `loading` mostra sempre uno spinner. */
	icon?: React.ReactNode;
	/** Messaggio mostrato sotto l'icona. */
	message?: React.ReactNode;
}

/** Segnaposto centrato per sezioni in caricamento, errore o senza dati. */
const StatusBlock = React.forwardRef<HTMLDivElement, StatusBlockProps>(
	({ className, kind, size, icon, message, children, ...props }, ref) => (
		<div
			ref={ref}
			role={kind === 'error' ? 'alert' : undefined}
			className={cn(statusBlockVariants({ kind, size, className }))}
			{...props}
		>
			{kind === 'loading' ? (
				<Loader2 className="h-6 w-6 animate-spin text-accent" />
			) : (
				icon && <span className="[&_svg]:h-6 [&_svg]:w-6 [&_svg]:opacity-30">{icon}</span>
			)}
			{message && <p>{message}</p>}
			{children}
		</div>
	),
);
StatusBlock.displayName = 'StatusBlock';

export { StatusBlock, statusBlockVariants };
